import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

/**
 * TransformationTimeline
 *
 * Vertical timeline showing the farm transformation journey:
 * phases, NDVI snapshots at each stage, and key milestones.
 */

const phaseIcons = {
  assessment: "🔍",
  infrastructure: "🔧",
  planting: "🌱",
  scaling: "📈",
  production: "🌾",
  research: "🧪",
  livestock: "🐄",
  default: "📍",
};

const getPhaseIcon = (phase) => {
  const lower = (phase || "").toLowerCase();
  if (lower.includes("assess") || lower.includes("survey") || lower.includes("design")) return phaseIcons.assessment;
  if (lower.includes("infra") || lower.includes("build") || lower.includes("deploy") || lower.includes("solar")) return phaseIcons.infrastructure;
  if (lower.includes("plant") || lower.includes("first")) return phaseIcons.planting;
  if (lower.includes("scal") || lower.includes("optim") || lower.includes("expan")) return phaseIcons.scaling;
  if (lower.includes("produc") || lower.includes("full") || lower.includes("commerc") || lower.includes("circular")) return phaseIcons.production;
  if (lower.includes("research") || lower.includes("lab") || lower.includes("r&d")) return phaseIcons.research;
  if (lower.includes("livestock") || lower.includes("cattle")) return phaseIcons.livestock;
  return phaseIcons.default;
};

const TransformationTimeline = ({ timeline, ndviSnapshots, isArabic }) => {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-40px" });

  if (!timeline || timeline.length === 0) return null;

  return (
    <div ref={ref} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
      <div className={`flex items-center gap-2 mb-6 ${isArabic ? "flex-row-reverse" : ""}`}>
        <span className="text-lg">🕐</span>
        <h3 className="text-base font-bold text-slate-900 mb-0">
          {isArabic ? "رحلة التحول" : "Transformation Journey"}
        </h3>
      </div>

      <ol className={`relative ${isArabic ? "border-r-2 mr-4" : "border-l-2 ml-4"} border-emerald-200 space-y-0`}>
        {timeline.map((phase, i) => {
          const isLast = i === timeline.length - 1;
          const snap = ndviSnapshots?.find((s) => s.year === phase.year);

          return (
            <motion.li
              key={phase.year}
              className={`${isArabic ? "mr-8" : "ml-8"} pb-8 last:pb-0`}
              initial={{ opacity: 0, x: isArabic ? 20 : -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: i * 0.15, duration: 0.5 }}
            >
              {/* Dot */}
              <span className={`absolute ${isArabic ? "-right-[13px]" : "-left-[13px]"} flex items-center justify-center w-6 h-6 rounded-full ${isLast ? "bg-emerald-500 ring-4 ring-emerald-100" : "bg-white border-2 border-emerald-400"} text-xs`}>
                {isLast ? "✓" : getPhaseIcon(phase.phase)}
              </span>

              {/* Content card */}
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 hover:shadow-md transition-shadow group">
                <div className={`flex items-center gap-2 mb-2 ${isArabic ? "flex-row-reverse" : ""}`}>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                    {phase.year}
                  </span>
                  <span className="text-sm font-bold text-slate-900">{phase.phase}</span>
                </div>
                <p className={`text-sm text-slate-600 leading-relaxed mb-0 ${isArabic ? "text-right" : ""}`}>
                  {phase.detail}
                </p>

                {/* NDVI snapshot for this phase */}
                {snap && (
                  <div className={`mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">NDVI</span>
                      <span className={`text-sm font-mono font-bold ${parseFloat(snap.ndvi) >= 0.4 ? "text-emerald-600" : "text-amber-600"}`}>
                        {snap.ndvi}
                      </span>
                    </div>
                    {snap.healthPct && (
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {isArabic ? "صحة" : "Health"}
                        </span>
                        <span className="text-sm font-bold text-slate-700">{snap.healthPct}%</span>
                      </div>
                    )}
                    {snap.note && (
                      <span className="text-[10px] text-slate-400 italic">{snap.note}</span>
                    )}
                  </div>
                )}
              </div>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
};

export default TransformationTimeline;
